<template>
  <v-dialog
    v-model="dialog"
    width="auto"
    :fullscreen="$vuetify.breakpoint.xsOnly"
  >
    <v-form ref="form" v-model="valid" :readonly="isReadOnly" @submit.prevent="submit()">
      <v-card>
        <v-card-title>
          <span class="headline">{{ formTitle }}</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <alert-message></alert-message>
            <v-row>
              <v-col :cols="$vuetify.breakpoint.xsOnly ? 2 : 1">
                <color-select v-model="form.color" :readonly="isReadOnly" />
              </v-col>
              <v-col :cols="$vuetify.breakpoint.xsOnly ? 10 : 11">
                <TaskSelect
                  v-model="src"
                  label="Select Job *"
                  :solo="false"
                  :readonly="isReadOnly"
                  @blur="$v.src.$touch()"
                  @watchObject="watchTask"
                  return-object
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-checkbox
                  v-model="form.isAllDay"
                  label="All Day"
                ></v-checkbox>
              </v-col>
            </v-row>
            <v-row v-if="form.isAllDay">
              <v-col cols="6">
                <date-picker
                  :disabled="isDatetimeDisabled"
                  v-model="form.startDate"
                  label="Start Date *"
                  @blur="$v.form.startDate.$touch()"
                  :error-messages="fieldErrors('form.startDate')"
                  :hide-details="true"
                  :readonly="isReadOnly"
                />
              </v-col>
              <v-col cols="6">
                <date-picker
                  :disabled="isDatetimeDisabled"
                  v-model="form.endDate"
                  label="End Date *"
                  @blur="$v.form.endDate.$touch()"
                  :error-messages="fieldErrors('form.endDate')"
                  :hide-details="true"
                  :readonly="isReadOnly"
                />
              </v-col>
            </v-row>
            <v-row v-else>
              <v-col cols="12" sm="12" md="6">
                <v-row>
                  <v-col cols="6">
                    <date-picker
                      :disabled="isDatetimeDisabled"
                      v-model="form.startDate"
                      label="Start Date *"
                      @blur="$v.form.startDate.$touch()"
                      :error-messages="fieldErrors('form.startDate')"
                      :hide-details="true"
                      :readonly="isReadOnly"
                    />
                  </v-col>
                  <v-col cols="6">
                    <time-picker
                      :disabled="isDatetimeDisabled"
                      v-model="form.startTime"
                      label="Time *"
                      @blur="$v.form.startTime.$touch()"
                      :error-messages="fieldErrors('form.startTime')"
                      :readonly="isReadOnly"
                    />
                  </v-col>
                </v-row>
              </v-col>

              <v-col cols="12" sm="12" md="6">
                <v-row>
                  <v-col cols="6">
                    <date-picker
                      :disabled="isDatetimeDisabled"
                      v-model="form.endDate"
                      label="End Date *"
                      @blur="$v.form.endDate.$touch()"
                      :error-messages="fieldErrors('form.endDate')"
                      :hide-details="true"
                      :readonly="isReadOnly"
                    />
                  </v-col>
                  <v-col cols="6">
                    <time-picker
                      :disabled="isDatetimeDisabled"
                      v-model="form.endTime"
                      label="Time *"
                      @blur="$v.form.endTime.$touch()"
                      :error-messages="fieldErrors('form.endTime')"
                      :readonly="isReadOnly"
                    />
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="12" md="12">
                <v-textarea
                  v-model="form.description"
                  label="Description"
                  @blur="$v.form.description.$touch()"
                  rows="3"
                  auto-grow
                ></v-textarea>
              </v-col>
              <v-col cols="12" sm="12" md="12">
                <v-autocomplete
                  v-model="form.employeeIds"
                  :items="employeeList"
                  multiple
                  dense
                  filled
                  @blur="$v.form.employeeIds.$touch()"
                  label="Employee"
                  :readonly="isReadOnly"
                ></v-autocomplete>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-btn
                  @click="viewJobDetail()"
                  v-if="item && item.id"
                  x-small
                  class="ml-2"
                  >Job Details</v-btn
                >
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-btn
            v-show="isEdit && hasAccess('schedule.delete')"
            color="error"
            :loading="loadingDelete"
            @click="deleteSchedule"
            >Delete</v-btn
          >
          <v-spacer></v-spacer>
          <v-btn color="darken-1" @click="cancel()">Cancel</v-btn>
          <v-btn
            v-show="hasAccess('schedule.edit')"
            :disabled="$v.$invalid"
            type="submit"
            color="info darken-1"
            :loading="loading"
          >
            {{ isEdit ? "Save" : "Add" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>

    <TaskForm
      :show="showJobDetail"
      :item="jobDetail"
      object="task"
      @onClosed="clearJobDetail()"
      @onSucceded="refreshAndClose()"
    />
  </v-dialog>
</template>

<script>
import UTIL from "src/helpers/utility";
import { mapActions, mapGetters, mapState } from "vuex";
import AlertMessage from "src/components/AlertMessage";
import { required } from "vuelidate/lib/validators";
import validationMixin from "@/mixins/validationMixin";
import DatePicker from "src/components/DatePicker.vue";
import formatterMixin from "@/mixins/formatterMixin";
import ColorSelect from "components/ColorSelect";
import TimePicker from "components/TimePickerTimestamp.vue";
import TaskSelect from "components/TaskSelect";
import TaskForm from "./_FormJob";

const defaultInputs = {
  id: "",
  taskId: "",
  startDate: "",
  startTime: "",
  endDate: "",
  endTime: "",
  description: "",
  employeeIds: [],
  color: "",
  isAllDay: false,
};

export default {
  components: {
    AlertMessage,
    DatePicker,
    ColorSelect,
    TimePicker,
    TaskSelect,
    TaskForm,
  },
  props: {
    item: Object,
    show: [Boolean, String],
  },
  mixins: [validationMixin, formatterMixin],
  validations() {
    if(this.form.isAllDay) {
      return {
        form: {
          id: {},
          taskId: { },
          startDate: { required },
          startTime: {},
          endDate: { required },
          endTime: {},
          description: {},
          employeeIds: {},
        },
        src: { required }
      }
    } else {
      return {
        form: {
          id: {},
          taskId: { },
          startDate: { required },
          startTime: { required },
          endDate: { required },
          endTime: { required },
          description: {},
          employeeIds: {},
        },
        src: { required }
      }
    }
  },
  validationMessages: {
    form: {
      startDate: { required: "Required" },
      startTime: { required: "Required" },
      endDate: { required: "Required" },
      endTime: { required: "Required" },
    },
  },

  data() {
    return {
      showDialog: this.show,
      isReadOnly: true,
      src: null,
      keepSchedColor: false,
      keepEmployees: false,
      jobDetail: {},
      form: {
        ...defaultInputs,
      },
      dialog: false,
      valid: false,
      loading: false,
      loadingDelete: false,
      showAttachFile: false,
      showJobDetail: false,
      materials: [],
      employeeList: [],
    };
  },

  watch: {
    async show(n, o) {
      if (n && !o) {
        this.dialog = true;
        await this.prepareTaskOptions();
      }
    },

    // selected job changed
    async src(newSrc, oldSrc) {
      if (newSrc && typeof newSrc === "number" && newSrc !== oldSrc) {
        await this.taskGetById(newSrc);

        if(this.keepSchedColor) {
          this.keepSchedColor = false;
        }
        else {
          this.form.color = this.currentTask?.color || "";
        }

        if(this.keepEmployees) {
          this.keepEmployees = false;
        } else {
          this.form.employees = [];
          this.form.employeeIds = [];
        }

        this.prepareEmployeeList();
      }
    },

    // recalc available employees when time changes
    utcDateTimeStart() {
      this.prepareEmployeeList();
    },
    utcDateTimeEnd() {
      this.prepareEmployeeList();
    },

    dialog(n, o) {
      if (!n && o) {
        this.onDialogClose();
      } else {
        this.onDialogOpen();
      }
    },

    item(newEvent, oldEvent) {
      if (
        newEvent &&
        newEvent.id &&
        JSON.stringify(newEvent) !== JSON.stringify(oldEvent)
      ) {
        this.processInputRaw(newEvent);
      } else {
        this.processInputRaw({
          ...newEvent,
        });
      }
    },
  },

  computed: {
    ...mapGetters("account", ["hasAccess"]),
    ...mapState({
      account: (state) => state.account,
      user: (state) => state.account.data.user,
      task: (state) => state.task,
      alert: (state) => state.alert,
      availableUsers: (state) => state.user.jobAvailable,
    }),
    formTitle() {
      let title = this.isReadOnly ? "Job Schedule" : "Schedule Job";
      if (this.currentTask && this.currentTask.code) {
        title += ` [${this.currentTask.code}]`;
      }
      return title;
    },
    isEdit() {
      return UTIL.gv(this.item, "id", "") ? true : false;
    },
    taskId() {
      return UTIL.gv(this.item, "id", "");
    },
    isDatetimeDisabled() {
      return false;
      // return this.isEdit ? true : false;
    },
    currentTask() {
      return this.task.single;
    },
    utcDateTimeStart() {
      // todo: handle invalid / cleared dates
      let start = `${this.form.startDate} ${this.form.startTime}`;
      return this.localToUtc(start);
    },
    utcDateTimeEnd() {
      // todo: handle invalid / cleared dates
      let end = `${this.form.endDate} ${this.form.endTime}`;
      return this.localToUtc(end);
    }
  },

  methods: {
    ...mapActions("schedule", ["create", "update", "delete"]),
    ...mapActions({
      createTimeClock: "timeclock/createFromTask",
      deleteTask: "schedule/delete",
      taskAddItemOption: "task/addItemOption",
      taskGetById: "task/getById",
      clearTask: "task/clearTask",
      getAllAvailableForJob:'user/getAllAvailableForJob',
    }),

    async prepareTaskOptions() {
      if (this.item && this.item.taskId) {
        await this.taskAddItemOption({
          PageSize: 1000,
          PageNumber: 1,
          SearchTerm: this.item.taskName,
        });
      }
    },

    watchTask(task) {
      if (task && task.id) {
        this.form.taskId = task.id;
        if (!this.form.description) {
          this.form.description = task.description;
        }
        this.prepareEmployeeList(task);
      }
    },

    async prepareEmployeeList() {
      // empty list if no selected job or if selected job has no assigned employees
      if (!this.currentTask || !this.currentTask.taskUsers) {
        this.employeeList = [];
        return;
      }

      // likewise, empty list if missing start and end time (needed to check employee availability)
      const start = this.utcDateTimeStart.format("YYYY-MM-DDThh:mm");
      const end = this.utcDateTimeEnd.format("YYYY-MM-DDThh:mm")
      if(start === "Invalid date" || end === "Invalid date") {
        this.employeeList = [];
        return;
      }

      // otherwise, filter employee list to available employees assigned to job
      const startFirst = start.valueOf() <= end.valueOf();
      const availabilityPayload = {
        dateStart: startFirst ? start : end,
        dateEnd: startFirst ? end : start,
      }
      if(this.form?.id) {
        availabilityPayload.exceptScheduleIds = this.form.id
      }
      await this.getAllAvailableForJob(availabilityPayload);
      
      this.employeeList = this.currentTask.taskUsers.filter((taskUser) => {
        return this.availableUsers.some(availUser => availUser.id === taskUser.userId);
      }).map((user) => {
        return {
          value: user.userId,
          text: `${user.userFirstName} ${user.userLastName}`,
        }
      }).sort((a, b) => (a.text.localeCompare(b.text)));
    },

    processInputRaw(taskObj) {
      const start = taskObj.startDate || this.localToUtc(new Date());
      const end = taskObj.endDate || this.localToUtc(new Date());
      let employeeIds = [];
      if(taskObj.employees && taskObj.employees.length > 0) {
        employeeIds = taskObj.employees.map(employee => employee.id);
      }


      this.form = {
        ...taskObj,
        employeeIds: employeeIds,
        color: taskObj.color,
        startDate: this.momentLocal(start).format("YYYY-MM-DD"),
        startTime: this.momentLocal(start).format("HH:mm"),
        endDate: this.momentLocal(end).format("YYYY-MM-DD"),
        endTime: this.momentLocal(end).format("HH:mm"),
      };

      if(taskObj?.color) {
        this.keepSchedColor = true;
      }
      this.keepEmployees = true;

      this.src = taskObj.taskId;
    },

    viewOrAddFile() {
      this.showAttachFile = true;
    },

    viewJobDetail() {
      this.showJobDetail = true;
      this.jobDetail = this.currentTask;
    },

    clearJobDetail() {
      this.showJobDetail = false;
    },

    clearAttachFile() {
      this.showAttachFile = false;
    },

    refreshAndClose() {
      this.$emit("onSucceded");
      this.dialog = false;
    },

    async deleteSchedule() {
      this.loadingDelete = true;
      if (!window.confirm("Delete schedule?")) {
        this.loadingDelete = false;
        return false;
      }
      let payload = {
        ...this.item,
      };
      this.$emit("onScheduleDeleted", this.item);
      await this.delete(payload);
      this.loadingDelete = false;
      this.dialog = false;
    },

    initForm() {},

    onDialogClose() {
      this.form =  {
        ...this.defaultInputs,
      };
      this.clearTask();
      this.$emit("onClosed");
    },

    onDialogOpen() {
      this.$store.dispatch("alert/clear");
      this.initForm();
      this.isReadOnly = (this.isEdit && !this.hasAccess('schedule.edit')) ||
        (!this.isEdit && !this.hasAccess('schedule.add'));
    },
    addNew() {
      this.dialog = true;
      this.form = {
        ...defaultInputs,
      };
    },
    completed() {
      this.$emit("onSucceded");
    },
    async submit() {
      this.loading = true;
      if (!this.valid) {
        this.$store.dispatch("snackbar/error", "Form invalid");
        return false;
      }


      const startFirst = this.utcDateTimeStart.valueOf() <= this.utcDateTimeEnd.valueOf();
      let payload = {
        ...this.form,
        startDate: startFirst ? this.utcDateTimeStart : this.utcDateTimeEnd,
        endDate: startFirst ? this.utcDateTimeEnd : this.utcDateTimeStart,
      };

      if (this.isEdit) {
        await this.update({
          id: this.taskId,
          ...payload,
        });
      } else {
        await this.create({
          ...payload,
        });
        if (!this.task.status.error) {
          // await this.createTimeClock({
          //   id: this.task.single.id,
          //   ...payload,
          // });
        }
      }

      if (!this.task.status.error) {
        this.dialog = false;
        this.completed();
      }
      this.loading = false;
    },

    cancel(){
      this.$emit("onCanceled");
      this.dialog = false;
    }
  },
};
</script>